import type { ReactNode } from 'react';

import { cn } from '@core/utils/cn';

export interface FieldProps {
  label: string;
  htmlFor?: string;
  /** Shown under the control when there is no error. */
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Label, control, and one line of explanation beneath.
 *
 * <p>Deliberately NOT Base UI's `Field`: validation here comes from react-hook-form and
 * from the server, and Base UI's Field owns validity through the native constraint API.
 * Running both means two sources of truth for whether a field is wrong, and the server's
 * answer — the one that actually matters for a duplicate device id — is the one the
 * native API cannot produce.
 *
 * <p>The error replaces the hint rather than stacking beneath it, so the control never
 * shifts other content when a message appears.
 */
export function Field({ label, htmlFor, hint, error, required, children, className }: FieldProps) {
  const describedBy = htmlFor ? `${htmlFor}-description` : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="text-fg block text-[13px] font-medium">
        {label}
        {required && (
          <span className="text-danger ml-0.5" aria-hidden>
            *
          </span>
        )}
      </label>

      {children}

      {(error || hint) && (
        <p
          id={describedBy}
          // Announced when it changes, because a form error that only exists visually is
          // invisible to anyone filling the form by keyboard and screen reader.
          role={error ? 'alert' : undefined}
          className={cn('text-[12px]', error ? 'text-danger' : 'text-fg-muted')}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
